/**
 * @fileoverview Componente responsável por montar o menu principal, atalhos e ações globais do layout autenticado.
 */

import React, { useContext, useEffect, useState, useRef } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { Tooltip, Popover, Avatar, Slider, Badge } from "@material-ui/core";
import { i18n, changeLanguage } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { Can } from "../components/Can";
import { SocketContext } from "../context/Socket/SocketContext";
import api from "../services/api";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useDashboard from "../hooks/useDashboard";
import {
  ChartNoAxesCombined,
  LayoutDashboard,
  BarChart3,
  Building,
  MessageCircle,
  Pen,
  UsersRound,
  UserRound,
  Tags,
  Link,
  Workflow,
  Settings,
  Menu,
  LogOut,
  Sun,
  Moon,
  Globe,
  Volume2,
  VolumeX,
  Volume1,
  ChevronRight,
  ChevronDown,
  Building2,
  Briefcase,
  Award,
  GitBranch,
  MessagesSquare,
  Bell,
  Newspaper,
  FileText,
  Cable,
  GraduationCap,
} from 'lucide-react';
import usePlans from "../hooks/usePlans";
import NoResponseOffendersModal from "../components/NoResponseOffendersModal";
import NewsDrawer from "../components/NewsDrawer";

const SquareKanbanIcon = ({ size = 20, strokeWidth = 1, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M8 7v7" />
    <path d="M12 7v4" />
    <path d="M16 7v9" />
  </svg>
);

const languages = [
  { code: 'pt', flag: '🇧🇷', label: 'PT' },
  { code: 'en', flag: '🇺🇸', label: 'EN' },
  { code: 'es', flag: '🇪🇸', label: 'ES' }
];

const useStyles = makeStyles((theme) => ({
  menuContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '8px 0',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 8px 16px',
    marginBottom: 4,
  },
  logo: {
    width: 24,
    height: 24,
    objectFit: 'contain',
  },
  mainMenu: {
    flex: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  spacer: {
    flex: 1,
  },
  bottomMenu: {
    flex: 0,
    borderTop: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    paddingTop: 8,
    marginTop: 8,
  },
  listItem: {
    borderRadius: '8px',
    margin: '0 8px',
    width: 'auto',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.04)',
      '& $listItemIcon': {
        color: theme.mode === 'light' ? '#666' : '#aaa',
      },
    },
  },
  listItemActive: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(30, 136, 229, 0.15)'
      : 'rgba(30, 136, 229, 0.1)',
    '& $listItemIcon': {
      color: '#1e88e5 !important',
    },
  },
  listItemIcon: {
    color: theme.mode === 'light' ? '#9f9f9f' : '#88898D',
    minWidth: 40,
    transition: 'color 0.15s ease',
  },
  listItemIconCollapsed: {
    minWidth: 'unset',
    margin: '0 auto',
  },
  listItemCollapsed: {
    justifyContent: 'center',
    paddingLeft: 8,
    paddingRight: 8,
  },
  listItemText: {
    color: theme.mode === 'light' ? '#000' : '#6D6F73',
    '& .MuiListItemText-primary': {
      color: theme.mode === 'light' ? '#000' : '#6D6F73',
      fontWeight: 500,
    },
  },

  // Dropdown Menu Styles
  dropdownTrigger: {
    cursor: 'pointer',
    position: 'relative',
  },
  menuAlertDot: {
    position: 'absolute',
    top: 1,
    right: 18,
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    zIndex: 2,
  },
  dropdownPaper: {
    width: 260,
    borderRadius: 16,
    overflow: 'hidden',
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(30, 32, 40, 0.98)'
      : 'rgba(255, 255, 255, 0.98)',
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 6px rgba(0, 0, 0, 0.3), 0 10px 40px rgba(0, 0, 0, 0.4)'
      : '0 4px 6px rgba(0, 0, 0, 0.07), 0 10px 40px rgba(0, 0, 0, 0.12)',
    marginLeft: 8,
  },
  dropdownContent: {
    padding: '8px',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.05)',
    },
  },
  dropdownItemActive: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(30, 136, 229, 0.15)'
      : 'rgba(30, 136, 229, 0.1)',
    '& $dropdownItemIcon': {
      color: '#1e88e5',
    },
    '& $dropdownItemLabel': {
      color: '#1e88e5',
    },
  },
  dropdownItemIcon: {
    color: theme.palette.type === 'dark' ? '#88898D' : '#9f9f9f',
  },
  dropdownItemLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#1a1a2e',
  },
  dropdownItemBadge: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#ef4444',
    color: '#fff',
    fontSize: 10,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
  },
  leadsBadge: {
    backgroundColor: '#5b7cff',
    color: '#fff',
  },
  newsBadge: {
    backgroundColor: '#5b7cff',
    minWidth: 8,
    height: 8,
    borderRadius: '50%',
    padding: 0,
    top: 2,
    right: 2,
  },
  dropdownDivider: {
    height: 1,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.06)'
      : 'rgba(0, 0, 0, 0.06)',
    margin: '6px 8px',
  },
  dropdownSectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#64748b' : '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '10px 14px 6px',
  },

  // Profile Section Styles
  profileButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 12,
    cursor: 'pointer',
    margin: '4px 8px',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.04)',
    },
  },
  profileAvatar: {
    width: 36,
    height: 36,
    fontSize: 14,
    fontWeight: 600,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.2)'
      : 'rgba(91, 124, 255, 0.1)',
    color: '#5b7cff',
  },
  profileAvatarCollapsed: {
    width: 32,
    height: 32,
    fontSize: 12,
    margin: '0 auto',
  },
  profileAvatarWrapper: {
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
  },
  profileStatusDot: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: '#22C55E',
    border: `2px solid ${theme.palette.type === 'dark' ? '#18181B' : '#ffffff'}`,
  },
  profileInfo: {
    flex: 1,
    minWidth: 0,
  },
  profileName: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#f1f5f9' : '#1a1a2e',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  profileRole: {
    fontSize: 11,
    color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
    fontWeight: 500,
  },

  // Profile Popover Styles
  profilePopover: {
    width: 280,
    borderRadius: 16,
    overflow: 'hidden',
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(30, 32, 40, 0.98)'
      : 'rgba(255, 255, 255, 0.98)',
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 6px rgba(0, 0, 0, 0.3), 0 10px 40px rgba(0, 0, 0, 0.4)'
      : '0 4px 6px rgba(0, 0, 0, 0.07), 0 10px 40px rgba(0, 0, 0, 0.12)',
    marginLeft: 8,
  },
  profilePopoverHeader: {
    padding: '16px',
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  profilePopoverAvatar: {
    width: 44,
    height: 44,
    fontSize: 16,
    fontWeight: 600,
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.2)'
      : 'rgba(91, 124, 255, 0.1)',
    color: '#5b7cff',
  },
  profilePopoverAvatarWrapper: {
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
  },
  profilePopoverStatusDot: {
    position: 'absolute',
    bottom: 1,
    right: 1,
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: '#22C55E',
    border: `2px solid ${theme.palette.type === 'dark' ? 'rgba(30,32,40,0.98)' : 'rgba(255,255,255,0.98)'}`,
  },
  profilePopoverInfo: {
    flex: 1,
    minWidth: 0,
  },
  profilePopoverName: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#f1f5f9' : '#1a1a2e',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  profilePopoverEmail: {
    fontSize: 12,
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  profilePopoverCompany: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    fontSize: 11,
    color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
    fontWeight: 500,
  },
  profilePopoverJobRole: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    fontSize: 11,
    color: theme.palette.type === 'dark' ? '#cbd5f5' : '#475569',
    fontWeight: 500,
  },
  section: {
    padding: '12px 16px',
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.06)',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sectionIcon: {
    width: 12,
    height: 12,
  },
  languagePills: {
    display: 'flex',
    gap: 6,
  },
  languagePill: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 6px',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(91, 124, 255, 0.1)'
        : 'rgba(91, 124, 255, 0.08)',
    },
  },
  languagePillSelected: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.15)'
      : 'rgba(91, 124, 255, 0.12)',
    borderColor: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.5)'
      : 'rgba(91, 124, 255, 0.4)',
  },
  languageFlag: {
    fontSize: 18,
    lineHeight: 1,
    marginBottom: 3,
  },
  languageCode: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
  },
  languageCodeSelected: {
    color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
  },
  themeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  themeLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.type === 'dark' ? '#f1f5f9' : '#1a1a2e',
  },
  themeToggle: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.04)',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(91, 124, 255, 0.15)'
        : 'rgba(91, 124, 255, 0.1)',
    },
  },
  themeIcon: {
    width: 18,
    height: 18,
    color: theme.palette.type === 'dark' ? '#fbbf24' : '#5b7cff',
  },
  volumeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  volumeValue: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
  },
  volumeSliderWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  volumeIcon: {
    width: 16,
    height: 16,
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
    },
  },
  volumeSlider: {
    flex: 1,
    color: '#5b7cff',
    '& .MuiSlider-track': {
      height: 3,
      borderRadius: 1.5,
    },
    '& .MuiSlider-rail': {
      height: 3,
      borderRadius: 1.5,
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)',
    },
    '& .MuiSlider-thumb': {
      width: 14,
      height: 14,
      backgroundColor: '#fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
  },
  actionsSection: {
    padding: '8px',
  },
  actionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.04)',
    },
  },
  actionItemDanger: {
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(239, 68, 68, 0.1)'
        : 'rgba(239, 68, 68, 0.08)',
    },
    '& $actionIcon': {
      color: '#ef4444',
    },
    '& $actionLabel': {
      color: '#ef4444',
    },
  },
  actionIcon: {
    width: 16,
    height: 16,
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
  },
  actionLabel: {
    flex: 1,
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.type === 'dark' ? '#f1f5f9' : '#1a1a2e',
  },
  actionArrow: {
    width: 14,
    height: 14,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
  },

  // No Response Indicator Styles
  noResponseIndicator: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '8px',
    marginBottom: 8,
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.1)',
      opacity: 0.8,
    },
  },
  indicatorImage: {
    width: collapsed => collapsed ? 32 : 40,
    height: collapsed => collapsed ? 32 : 40,
    objectFit: 'contain',
    transition: 'all 0.3s ease',
  },
}));


function ListItemLink(props) {
  const { icon, primary, to, className, collapsed } = props;
  const classes = useStyles();
  const location = useLocation();

  const isActive = location.pathname === to || (to === '/' && location.pathname === '/');

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <Tooltip title={collapsed ? primary : ""} placement="right" arrow>
        <ListItem
          button
          dense
          component={renderLink}
          className={`${classes.listItem} ${isActive ? classes.listItemActive : ''} ${collapsed ? classes.listItemCollapsed : ''} ${className || ''}`}
        >
          {icon ? (
            <ListItemIcon className={`${classes.listItemIcon} ${collapsed ? classes.listItemIconCollapsed : ''}`}>
              {icon}
            </ListItemIcon>
          ) : null}
          {!collapsed && <ListItemText primary={primary} className={classes.listItemText} />}
        </ListItem>
      </Tooltip>
    </li>
  );
}

const MainListItems = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    drawerClose,
    collapsed,
    user,
    volume,
    setVolume,
    toggleColorMode,
    onOpenProfile,
    onLogout
  } = props;

  const isInternalOnly = user?.accessScope === "internal";
  const isAttendanceOnly = user?.accessScope === "attendance";

  const { whatsApps } = useContext(WhatsAppsContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const { find: findDashboard } = useDashboard();

  const socketManager = useContext(SocketContext);
  const { getPlanCompany } = usePlans();
  const location = useLocation();

  // No Response Indicator states
  const [noResponseRate, setNoResponseRate] = useState(0);
  const [showIndicator, setShowIndicator] = useState(true);
  const [offendersModalOpen, setOffendersModalOpen] = useState(false);
  // Academy (Área de Treinamento) — default ligado (só "disabled" esconde)
  const [showAcademy, setShowAcademy] = useState(true);

  // News Drawer states
  const [newsDrawerOpen, setNewsDrawerOpen] = useState(false);
  const [hasNewPosts, setHasNewPosts] = useState(false);

  // Leads states
  const [showLeads, setShowLeads] = useState(false);
  const [unreadLeadsCount, setUnreadLeadsCount] = useState(0);

  // Chat Interno — nº de canais com mensagens não lidas (não soma de msgs)
  // Set de canais com mensagens não lidas (o badge usa `.size`). Set em vez de
  // contador p/ poder atualizar LOCALMENTE ao ler um canal, sem refetch.
  const [unreadChannelIds, setUnreadChannelIds] = useState(() => new Set());

  // Dropdown states
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [produtividadeAnchorEl, setProdutividadeAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const profileOpen = Boolean(profileAnchorEl);
  const produtividadeOpen = Boolean(produtividadeAnchorEl);
  const produtividadeHoverTimeoutRef = useRef(null);

  // Profile settings
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem('i18nextLng') || 'pt'
  );

  const isDark = theme.palette.type === 'dark';

  // Hover timeout refs
  const menuHoverTimeoutRef = useRef(null);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  // Fetch plan configs (for showLeads feature flag)
  useEffect(() => {
    async function fetchData() {
      if (!user?.companyId) {
        return;
      }

      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowLeads(planConfigs?.plan?.useLeads !== false);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch leads unviewed count
  useEffect(() => {
    if (!user?.companyId) return;

    const fetchStats = async () => {
      try {
        const { data } = await api.get("/external-leads/stats");
        setUnreadLeadsCount(data.unviewed || 0);
      } catch (err) {
        // Silenciar erro
      }
    };

    fetchStats();
  }, [user?.companyId]);

  // Socket listener for leads updates
  useEffect(() => {
    if (!user?.companyId) return;

    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);
    const eventName = `company-${companyId}-externalLead`;

    const handleExternalLead = async () => {
      try {
        const response = await api.get("/external-leads/stats");
        setUnreadLeadsCount(response.data.unviewed || 0);
      } catch (err) {
        console.error("[MainListItems] Erro ao buscar stats:", err);
      }
    };

    socket.on(eventName, handleExternalLead);

    return () => {
      socket.off(eventName, handleExternalLead);
    };
  }, [socketManager, user?.companyId]);

  // Chat Interno: fetch inicial + socket listener para badge de canais não lidos.
  // Backend já emite em `user-${userId}` com:
  //   - action: "newInternalMessage" (CreateMessageService) quando chega msg
  //   - action: "internalChannelRead" (MarkChannelReadService) quando user lê
  // Re-fetch é debounced para não martelar em rajada de mensagens.
  useEffect(() => {
    if (!user?.companyId || !user?.id) return undefined;
    if (isAttendanceOnly) return undefined; // user attendance não vê chat interno

    const fetchInternalUnread = async () => {
      try {
        const { data } = await api.get("/internal-chat/unread-counts");
        setUnreadChannelIds(new Set(Object.keys(data?.channels || {}).map(Number)));
      } catch (err) {
        // silencia: sidebar não deve quebrar se endpoint falhar
      }
    };

    fetchInternalUnread();

    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);
    const userEvent = `user-${user.id}`;
    let debounceTimer;

    const handleInternalEvent = (data) => {
      if (!data) return;
      // Ler um canal (abrir a conversa) é resolvido LOCALMENTE: só remove do set,
      // sem refazer GET /unread-counts a cada troca de conversa.
      if (data.action === "internalChannelRead") {
        const chId = Number(data.channelId);
        setUnreadChannelIds((prev) => {
          if (!prev.has(chId)) return prev;
          const next = new Set(prev);
          next.delete(chId);
          return next;
        });
        return;
      }
      // Mensagem nova: o backend é a fonte da verdade (exclui canal aberto/autor)
      // → revalida com debounce.
      if (data.action === "newInternalMessage") {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(fetchInternalUnread, 600);
      }
    };

    socket.on(userEvent, handleInternalEvent);
    return () => {
      socket.off(userEvent, handleInternalEvent);
      clearTimeout(debounceTimer);
    };
  }, [socketManager, user?.companyId, user?.id, isAttendanceOnly]);


  // Fetch no response indicator setting
  useEffect(() => {
    const fetchIndicatorSetting = async () => {
      try {
        const { data } = await api.get("/settings");
        const indicatorSetting = data.find((s) => s.key === "noResponseIndicator");
        setShowIndicator(indicatorSetting?.value === "enabled");
        const academySetting = data.find((s) => s.key === "enableAcademy");
        setShowAcademy(academySetting?.value !== "disabled");
      } catch (err) {
        console.error("[MainListItems] Erro ao buscar configuração do indicador:", err);
      }
    };

    fetchIndicatorSetting();
  }, []);

  // Fetch dashboard data for no response rate
  useEffect(() => {
    if (!user?.companyId || !showIndicator) return;

    const fetchDashboardData = async () => {
      try {
        const data = await findDashboard();
        setNoResponseRate(data?.counters?.noResponseRate || 0);
      } catch (err) {
        console.error("[MainListItems] Erro ao buscar dados do dashboard:", err);
      }
    };

    fetchDashboardData();

    // Atualizar a cada 2 minutos
    const interval = setInterval(fetchDashboardData, 120000);

    return () => clearInterval(interval);
  }, [user?.companyId, showIndicator, findDashboard]);

  // Listen for settings changes via socket
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-settings`, (data) => {
      if (data.action === "update" && data.setting.key === "noResponseIndicator") {
        setShowIndicator(data.setting.value === "enabled");
      }
      if (data.action === "update" && data.setting.key === "enableAcademy") {
        setShowAcademy(data.setting.value !== "disabled");
      }
    });

    return () => {
      socket.off(`company-${companyId}-settings`);
    };
  }, [socketManager]);

  // Check for new news posts
  useEffect(() => {
    const fetchNewsPosts = async () => {
      try {
        const { data } = await api.get("/news-posts/", { params: { pageNumber: 1 } });
        const lastSeen = localStorage.getItem("newsPostsLastSeen");
        if (data.records?.length > 0) {
          const latestPost = data.records[0];
          if (!lastSeen || new Date(latestPost.createdAt) > new Date(lastSeen)) {
            setHasNewPosts(true);
          }
        }
      } catch (err) {
        // silent
      }
    };
    fetchNewsPosts();
  }, []);

  // Menu dropdown handlers
  const handleMenuEnter = (event) => {
    clearTimeout(menuHoverTimeoutRef.current);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuLeave = () => {
    menuHoverTimeoutRef.current = setTimeout(() => {
      setMenuAnchorEl(null);
    }, 300);
  };

  const handleMenuPopoverEnter = () => {
    clearTimeout(menuHoverTimeoutRef.current);
  };

  const handleMenuPopoverLeave = () => {
    setMenuAnchorEl(null);
  };

  // Produtividade dropdown handlers (Dashboard / Multi unidades)
  // Click-based (não hover) — alinha com expectativa do usuário e evita
  // que tooltip e popover briguem. Tooltip "Produtividade" é suprimido
  // enquanto o dropdown está aberto.
  const handleProdToggle = (event) => {
    setProdutividadeAnchorEl((current) => current ? null : event.currentTarget);
  };

  const handleProdClose = () => {
    setProdutividadeAnchorEl(null);
  };

  // Profile handlers
  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLanguageChange = async (langCode) => {
    setSelectedLanguage(langCode);
    changeLanguage(langCode);
    try {
      await api.post(`/users/set-language/${langCode}`);
    } catch (error) {
      console.error(error);
    }
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
    localStorage.setItem("volume", newValue);
  };

  const handleMuteToggle = () => {
    const newVolume = volume === 0 ? 0.7 : 0;
    setVolume(newVolume);
    localStorage.setItem("volume", newVolume);
  };

  const getVolumeIcon = () => {
    if (volume === 0) return VolumeX;
    if (volume < 0.5) return Volume1;
    return Volume2;
  };

  const VolumeIcon = getVolumeIcon();

  // Paleta UNIFICADA — bate com o chat interno
  const stringToColor = (str) => {
    if (!str) return "#9e9e9e";
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    const colors = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#6366f1", "#f43f5e"];
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserJobRoleLabel = (currentUser) => {
    return currentUser?.jobRole?.name
      || i18n.t("mainDrawer.appBar.noJobRole")
      || "Sem cargo";
  };

  const isPathActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Get indicator image based on noResponseRate
  const getIndicatorImage = () => {
    if (noResponseRate > 30) {
      return require('../assets/indicators/indicator-red-30plus.png');
    } else if (noResponseRate >= 25) {
      return require('../assets/indicators/indicator-orange-25-30.png');
    } else if (noResponseRate >= 15) {
      return require('../assets/indicators/indicator-green-15-25.png');
    } else {
      return require('../assets/indicators/indicator-darkgreen-under15.png');
    }
  };

  return (
    <div className={classes.menuContainer} onClick={drawerClose}>
      {/* Logo */}
      <div className={classes.logoContainer}>
        <img
          src="/favicon.ico"
          alt="Sttelar"
          className={classes.logo}
        />
      </div>

      {/* Main Menu */}
      <ul className={classes.mainMenu}>
        {!isInternalOnly && (
          <ListItemLink
            to="/tickets"
            primary={i18n.t("mainDrawer.listItems.tickets")}
            icon={<MessageCircle size={22} strokeWidth={1.5} />}
            collapsed={collapsed}
          />
        )}

        {!isInternalOnly && (
          <ListItemLink
            to="/contacts"
            primary={i18n.t("mainDrawer.listItems.contacts")}
            icon={<UserRound size={22} strokeWidth={1.5} />}
            collapsed={collapsed}
          />
        )}

        <Can
          role={user?.profile}
          perform="dashboard:view"
          yes={() => {
            const isProdActive = isPathActive('/multi-unidades') || location.pathname === '/';
            // Tooltip suprimido enquanto o dropdown está aberto pra não
            // sobrepor o popover.
            const tooltipTitle = (collapsed && !produtividadeOpen) ? "Produtividade" : "";
            return (
              <Tooltip title={tooltipTitle} placement="right" arrow>
                <li>
                  <ListItem
                    button
                    dense
                    onClick={handleProdToggle}
                    className={`${classes.listItem} ${isProdActive ? classes.listItemActive : ''} ${produtividadeOpen ? classes.listItemActive : ''} ${collapsed ? classes.listItemCollapsed : ''}`}
                  >
                    <ListItemIcon className={`${classes.listItemIcon} ${collapsed ? classes.listItemIconCollapsed : ''}`}>
                      <BarChart3 size={22} strokeWidth={1.5} />
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <ListItemText primary="Produtividade" className={classes.listItemText} />
                        <ChevronDown
                          size={15}
                          strokeWidth={1.75}
                          style={{
                            color: 'inherit',
                            opacity: 0.6,
                            transition: 'transform 0.2s ease',
                            transform: produtividadeOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            marginRight: 4,
                          }}
                        />
                      </>
                    )}
                  </ListItem>
                </li>
              </Tooltip>
            );
          }}
        />
        {/* Ranking ocultado por enquanto
        <ListItemLink
          to="/ranking"
          primary="Ranking"
          icon={<Award size={22} strokeWidth={1.5} />}
          collapsed={collapsed}
        />*/}

        {showLeads && (
          <ListItemLink
            to="/crm"
            primary="Boards"
            icon={
              <Badge
                badgeContent={unreadLeadsCount}
                color="secondary"
                max={99}
                classes={{ badge: classes.leadsBadge }}
              >
                <SquareKanbanIcon size={22} strokeWidth={1.5} />
              </Badge>
            }
            collapsed={collapsed}
          />
        )}

        {!isAttendanceOnly && (
          <ListItemLink
            to="/internal-chat"
            primary="Chat Interno"
            icon={
              <Badge
                badgeContent={unreadChannelIds.size}
                color="secondary"
                max={99}
                classes={{ badge: classes.leadsBadge }}
              >
                <MessagesSquare size={22} strokeWidth={1.5} />
              </Badge>
            }
            collapsed={collapsed}
          />
        )}

        {user?.super && (
          <ListItemLink
            to="/companies"
            primary="Empresas"
            icon={<Building2 size={22} strokeWidth={1.5} />}
            collapsed={collapsed}
          />
        )}
      </ul>

      {/* Spacer */}
      <div className={classes.spacer} />

      {/* News / Novidades Bell - above the divider */}
      <Tooltip title={collapsed ? "Novidades" : ""} placement="right" arrow>
        <ListItem
          button
          dense
          onClick={() => {
            setNewsDrawerOpen(true);
            setHasNewPosts(false);
            localStorage.setItem("newsPostsLastSeen", new Date().toISOString());
          }}
          className={`${classes.listItem} ${collapsed ? classes.listItemCollapsed : ''}`}
        >
          <ListItemIcon className={`${classes.listItemIcon} ${collapsed ? classes.listItemIconCollapsed : ''}`}>
            <Badge
              variant="dot"
              invisible={!hasNewPosts}
              overlap="rectangular"
              classes={{ badge: classes.newsBadge }}
            >
              <Bell size={22} strokeWidth={1.5} />
            </Badge>
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Novidades" className={classes.listItemText} />}
        </ListItem>
      </Tooltip>

      {/* Bottom Menu */}
      <div className={classes.bottomMenu}>
        {/* No Response Indicator */}
        {showIndicator && !isInternalOnly && (
          <Tooltip
            title={`Sem resposta +10min: ${noResponseRate}%`}
            placement="right"
            arrow
          >
            <div
              className={classes.noResponseIndicator}
              onClick={() => setOffendersModalOpen(true)}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={getIndicatorImage()}
                alt={`Indicador ${noResponseRate}%`}
                className={classes.indicatorImage}
                style={{
                  width: collapsed ? 32 : 40,
                  height: collapsed ? 32 : 40
                }}
              />
            </div>
          </Tooltip>
        )}

        {/* More Menu Trigger */}
	      <Tooltip title={collapsed ? "Menu" : ""} placement="right" arrow>
	        <div
	          className={classes.dropdownTrigger}
	          onMouseEnter={handleMenuEnter}
	          onMouseLeave={handleMenuLeave}
	        >
	          {connectionWarning && <span className={classes.menuAlertDot} />}
	          <ListItem
	            button
	            dense
	            className={`${classes.listItem} ${collapsed ? classes.listItemCollapsed : ''}`}
	          >
              <ListItemIcon className={`${classes.listItemIcon} ${collapsed ? classes.listItemIconCollapsed : ''}`}>
                <Menu size={22} strokeWidth={1.5} />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Menu" className={classes.listItemText} />}
            </ListItem>
          </div>
        </Tooltip>

        {/* More Menu Popover */}
        <Popover
          open={menuOpen}
          anchorEl={menuAnchorEl}
          onClose={() => setMenuAnchorEl(null)}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          PaperProps={{
            className: classes.dropdownPaper,
            onMouseEnter: handleMenuPopoverEnter,
            onMouseLeave: handleMenuPopoverLeave,
            style: { pointerEvents: 'auto' },
          }}
          style={{ pointerEvents: 'none' }}
          disableRestoreFocus
          disableScrollLock
        >
          <div className={classes.dropdownContent}>
            {/* Quick Messages */}
            {!isInternalOnly && (
              <RouterLink to="/quick-messages" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                <div className={`${classes.dropdownItem} ${isPathActive('/quick-messages') ? classes.dropdownItemActive : ''}`}>
                  <Pen size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                  <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.quickMessages")}</span>
                </div>
              </RouterLink>
            )}

            {/* Tags */}
            {!isInternalOnly && (
              <RouterLink to="/tags" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                <div className={`${classes.dropdownItem} ${isPathActive('/tags') ? classes.dropdownItemActive : ''}`}>
                  <Tags size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                  <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.tags")}</span>
                </div>
              </RouterLink>
            )}

            {/* Academy */}
            {showAcademy && (
              <RouterLink to="/academy" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                <div className={`${classes.dropdownItem} ${isPathActive('/academy') ? classes.dropdownItemActive : ''}`}>
                  <GraduationCap size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                  <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.academy")}</span>
                </div>
              </RouterLink>
            )}

            {/* Admin Section */}
            <Can
              role={user?.profile}
              perform="drawer-admin-items:view"
              yes={() => (
                <>
                  <div className={classes.dropdownDivider} />
                  <div className={classes.dropdownSectionTitle}>
                    {i18n.t("mainDrawer.listItems.administration")}
                  </div>

                  {/* Connections */}
                  {!isInternalOnly && (
                    <RouterLink to="/connections" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                      <div className={`${classes.dropdownItem} ${isPathActive('/connections') ? classes.dropdownItemActive : ''}`}>
                        <Link size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                        <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.connections")}</span>
                        {connectionWarning && <span className={classes.dropdownItemBadge}>!</span>}
                      </div>
                    </RouterLink>
                  )}

                  {/* Queues */}
                  {!isInternalOnly && (
                    <RouterLink to="/queues" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                      <div className={`${classes.dropdownItem} ${isPathActive('/queues') ? classes.dropdownItemActive : ''}`}>
                        <Workflow size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                        <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.queues")}</span>
                      </div>
                    </RouterLink>
                  )}

                  {/* Users */}
                  <RouterLink to="/users" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                    <div className={`${classes.dropdownItem} ${isPathActive('/users') ? classes.dropdownItemActive : ''}`}>
                      <UsersRound size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                      <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.users")}</span>
                    </div>
                  </RouterLink>

                  {/* Fluxo */}
                  {!isInternalOnly && (
                    <RouterLink to="/fluxo" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                      <div className={`${classes.dropdownItem} ${isPathActive('/fluxo') ? classes.dropdownItemActive : ''}`}>
                        <GitBranch size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                        <span className={classes.dropdownItemLabel}>Fluxo</span>
                      </div>
                    </RouterLink>
                  )}

                  {/* Meta Templates */}
                  {!isInternalOnly && (
                    <RouterLink to="/meta-templates" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                      <div className={`${classes.dropdownItem} ${isPathActive('/meta-templates') ? classes.dropdownItemActive : ''}`}>
                        <FileText size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                        <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.metaTemplates")}</span>
                      </div>
                    </RouterLink>
                  )}

                  {/* Integrações */}
                  {!isInternalOnly && (
                    <RouterLink to="/integrations" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                      <div className={`${classes.dropdownItem} ${isPathActive('/integrations') ? classes.dropdownItemActive : ''}`}>
                        <Cable size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                        <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.integrations")}</span>
                      </div>
                    </RouterLink>
                  )}

                  {/* Settings */}
                  <RouterLink to="/settings" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                    <div className={`${classes.dropdownItem} ${isPathActive('/settings') ? classes.dropdownItemActive : ''}`}>
                      <Settings size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                      <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.settings")}</span>
                    </div>
                  </RouterLink>

                  {/* Super Dashboard */}
                  {user?.super && (
                    <RouterLink to="/super-dashboard" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                      <div className={`${classes.dropdownItem} ${isPathActive('/super-dashboard') ? classes.dropdownItemActive : ''}`}>
                        <LayoutDashboard size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                        <span className={classes.dropdownItemLabel}>Super Dashboard</span>
                      </div>
                    </RouterLink>
                  )}

                  {/* Admin Panel */}
                  {user?.super && (
                    <RouterLink to="/super-admin-panel" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                      <div className={`${classes.dropdownItem} ${isPathActive('/super-admin-panel') ? classes.dropdownItemActive : ''}`}>
                        <Building2 size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                        <span className={classes.dropdownItemLabel}>Admin Panel</span>
                      </div>
                    </RouterLink>
                  )}

                  {/* Gerenciar Novidades */}
                  {user?.super && (
                    <RouterLink to="/news-posts" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                      <div className={`${classes.dropdownItem} ${isPathActive('/news-posts') ? classes.dropdownItemActive : ''}`}>
                        <Newspaper size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                        <span className={classes.dropdownItemLabel}>Gerenciar Novidades</span>
                      </div>
                    </RouterLink>
                  )}

                  {/* Gerenciar Academy */}
                  {user?.super && (
                    <RouterLink to="/academy/manage" style={{ textDecoration: 'none' }} onClick={() => setMenuAnchorEl(null)}>
                      <div className={`${classes.dropdownItem} ${isPathActive('/academy/manage') ? classes.dropdownItemActive : ''}`}>
                        <GraduationCap size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                        <span className={classes.dropdownItemLabel}>{i18n.t("mainDrawer.listItems.manageAcademy")}</span>
                      </div>
                    </RouterLink>
                  )}
                </>
              )}
            />
          </div>
        </Popover>

        {/* Produtividade Popover (Dashboard / Multi unidades) — click-based */}
        <Popover
          open={produtividadeOpen}
          anchorEl={produtividadeAnchorEl}
          onClose={handleProdClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{ className: classes.dropdownPaper }}
          disableRestoreFocus
          disableScrollLock
        >
          <div className={classes.dropdownContent}>
            <div className={classes.dropdownSectionTitle}>Produtividade</div>
            <RouterLink to="/" style={{ textDecoration: 'none' }} onClick={handleProdClose}>
              <div className={`${classes.dropdownItem} ${location.pathname === '/' ? classes.dropdownItemActive : ''}`}>
                <ChartNoAxesCombined size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                <span className={classes.dropdownItemLabel}>Dashboard</span>
              </div>
            </RouterLink>
            <RouterLink to="/multi-unidades" style={{ textDecoration: 'none' }} onClick={handleProdClose}>
              <div className={`${classes.dropdownItem} ${isPathActive('/multi-unidades') ? classes.dropdownItemActive : ''}`}>
                <Building size={18} strokeWidth={1.5} className={classes.dropdownItemIcon} />
                <span className={classes.dropdownItemLabel}>Multi unidades</span>
              </div>
            </RouterLink>
            {/* Item "Dashboard Geral" removido — conteudo foi unificado
                em "Multi unidades" > sub-aba "Graficos operacionais".
                Rota /dashboard-geral preservada como Redirect para compat
                de bookmarks. */}
          </div>
        </Popover>

        {/* Profile Button */}
        <Tooltip title={collapsed ? user?.name || "Perfil" : ""} placement="right" arrow>
          <div className={classes.profileButton} onClick={handleProfileClick}>
            <div className={classes.profileAvatarWrapper}>
              {user?.profileImage ? (
                <Avatar
                  src={user.profileImage}
                  className={`${classes.profileAvatar} ${collapsed ? classes.profileAvatarCollapsed : ''}`}
                />
              ) : (
                <Avatar style={{ backgroundColor: stringToColor(user?.name), color: "#fff" }} className={`${classes.profileAvatar} ${collapsed ? classes.profileAvatarCollapsed : ''}`}>
                  {getInitials(user?.name)}
                </Avatar>
              )}
              <span className={classes.profileStatusDot} />
            </div>
            {!collapsed && (
              <div className={classes.profileInfo}>
                <div className={classes.profileName}>{user?.name || 'Usuário'}</div>
                <div className={classes.profileRole}>
                  {user?.profile === 'admin' ? 'Administrador' : 'Usuário'}
                </div>
              </div>
            )}
          </div>
        </Tooltip>

        {/* Profile Popover */}
        <Popover
          open={profileOpen}
          anchorEl={profileAnchorEl}
          onClose={handleProfileClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          PaperProps={{
            className: classes.profilePopover,
          }}
          disableRestoreFocus
        >
          {/* User Header */}
          <div className={classes.profilePopoverHeader}>
            <div className={classes.profilePopoverAvatarWrapper}>
              {user?.profileImage ? (
                <Avatar src={user.profileImage} className={classes.profilePopoverAvatar} />
              ) : (
                <Avatar style={{ backgroundColor: stringToColor(user?.name), color: "#fff" }} className={classes.profilePopoverAvatar}>
                  {getInitials(user?.name)}
                </Avatar>
              )}
              <span className={classes.profilePopoverStatusDot} />
            </div>
            <div className={classes.profilePopoverInfo}>
              <div className={classes.profilePopoverName}>{user?.name || 'Usuário'}</div>
              <div className={classes.profilePopoverEmail}>{user?.email || ''}</div>
              {/* Company Info */}
              <div className={classes.profilePopoverCompany}>
                <Building2 size={12} />
                <span>{user?.company?.name || 'Empresa'}</span>
              </div>
              <div className={classes.profilePopoverJobRole}>
                <Briefcase size={12} />
                <span>{getUserJobRoleLabel(user)}</span>
              </div>
            </div>
          </div>

          {/* Company Switcher Section */}
          {user?.companies?.length > 1 && (
            <div className={classes.section}>
              <div className={classes.sectionLabel}>
                <Building2 className={classes.sectionIcon} />
                Empresa ativa
              </div>
              <select
                value={user.companyId}
                onChange={async (e) => {
                  const companyId = Number(e.target.value);
                  if (companyId === user.companyId) return;
                  try {
                    const { data } = await api.post("/auth/switch-company", { companyId });
                    localStorage.setItem("token", data.token);
                    localStorage.setItem("companyId", data.user.companyId);
                    window.location.reload();
                  } catch (err) {
                    console.error("Erro ao trocar empresa:", err);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(91, 124, 255, 0.25)",
                  background: "rgba(91, 124, 255, 0.08)",
                  color: "#e8eaed",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  outline: "none",
                  appearance: "none",
                  WebkitAppearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 10px center",
                  paddingRight: 30,
                }}
              >
                {user.companies.map((c) => (
                  <option key={c.id} value={c.id} style={{ background: "#1a1a2e", color: "#e8eaed" }}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Language Section */}
          <div className={classes.section}>
            <div className={classes.sectionLabel}>
              <Globe className={classes.sectionIcon} />
              {i18n.t("mainDrawer.appBar.language") || "Idioma"}
            </div>
            <div className={classes.languagePills}>
              {languages.map((lang) => (
                <div
                  key={lang.code}
                  className={`${classes.languagePill} ${
                    selectedLanguage === lang.code ? classes.languagePillSelected : ''
                  }`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <span className={classes.languageFlag}>{lang.flag}</span>
                  <span className={`${classes.languageCode} ${
                    selectedLanguage === lang.code ? classes.languageCodeSelected : ''
                  }`}>
                    {lang.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Theme Section */}
          <div className={classes.section}>
            <div className={classes.themeRow}>
              <span className={classes.themeLabel}>
                {i18n.t("mainDrawer.appBar.appearance") || "Aparência"}
              </span>
              <div className={classes.themeToggle} onClick={toggleColorMode}>
                {isDark ? (
                  <Sun className={classes.themeIcon} />
                ) : (
                  <Moon className={classes.themeIcon} />
                )}
              </div>
            </div>
          </div>

          {/* Volume Section */}
          <div className={classes.section}>
            <div className={classes.volumeHeader}>
              <div className={classes.sectionLabel} style={{ marginBottom: 0 }}>
                <Volume2 className={classes.sectionIcon} />
                {i18n.t("mainDrawer.appBar.notificationVolume") || "Som"}
              </div>
              <span className={classes.volumeValue}>{Math.round(volume * 100)}%</span>
            </div>
            <div className={classes.volumeSliderWrapper}>
              <VolumeIcon
                className={classes.volumeIcon}
                onClick={handleMuteToggle}
              />
              <Slider
                className={classes.volumeSlider}
                value={volume}
                min={0}
                max={1}
                step={0.05}
                onChange={handleVolumeChange}
              />
              <Volume2 className={classes.volumeIcon} style={{ opacity: 0.5 }} />
            </div>
          </div>

          {/* Actions Section */}
          <div className={classes.actionsSection}>
            <div
              className={classes.actionItem}
              onClick={() => { onOpenProfile(); handleProfileClose(); }}
            >
              <UserRound className={classes.actionIcon} />
              <span className={classes.actionLabel}>
                {i18n.t("mainDrawer.appBar.user.profile") || "Meu Perfil"}
              </span>
              <ChevronRight className={classes.actionArrow} />
            </div>
            <div
              className={`${classes.actionItem} ${classes.actionItemDanger}`}
              onClick={() => { onLogout(); handleProfileClose(); }}
            >
              <LogOut className={classes.actionIcon} />
              <span className={classes.actionLabel}>
                {i18n.t("mainDrawer.appBar.user.logout") || "Sair"}
              </span>
            </div>
          </div>
        </Popover>

      </div>

      {/* Modal de Usuários com Tickets Sem Resposta */}
      <NoResponseOffendersModal
        open={offendersModalOpen}
        onClose={() => setOffendersModalOpen(false)}
      />

      {/* News Drawer */}
      <NewsDrawer
        open={newsDrawerOpen}
        onClose={() => setNewsDrawerOpen(false)}
      />
    </div>
  );
};

export default MainListItems;
